// The WGSL twins of `../geo.ts`: the outline wash and the outline glow
// (R495), the border that ships (R532; `#border=geo` is the knob's default).
//
// Both programs draw a MESH the CPU traces, so both own a vertex entry, and
// the vertex input struct is GENERATED from the same `AttribSpec` list the
// WebGL2 vertex array is built from. One list states the locations for the
// GLSL source, the vertex buffer and this twin.
//
// The stroke declares its style figures in the VERTEX stage. WGSL has one
// block for both stages, so the vertex entry reads them as `U.uStripWidth`
// and the fragment entry reads its own as `U.uPassAlpha`, out of the very
// same struct. That is what a generated header buys: a uniform is declared
// once and each stage takes what it needs.
//
// The stroke also carries two varyings the shared `VsOut` has no field for —
// the signed distance to the outline and the place along the loop that the
// faction dash reads (R1577) — so the stroke states a varying struct of its
// own. The fill takes the shared one.
//
// The consts below are BODIES: the functions and the two entry points, with
// no uniform text at all. The builders put the generated header, the shared
// prelude and (for the fill) the rock mask in front of them, the way the GLSL
// half concatenates `PRELUDE` and `ROCK_PLANE`.

import {
  GEO_FILL_ALL_UNIFORMS, GEO_FILL_ATTRIBS,
  GEO_STROKE_ALL_UNIFORMS, GEO_STROKE_ATTRIBS,
} from '../geo';
import { wgslModule } from './module';
import { PRELUDE_BODY_WGSL } from './prelude';
import { DASH_ALONG_WGSL, ROCK_PLANE_WGSL } from './territory';

// ---------------------------------------------------------------------------
// The fill
// ---------------------------------------------------------------------------

/**
 * The fill's vertex entry: one world point a vertex, and no more. Its input
 * struct is generated from the attribute list by `wgslModule`.
 *
 * `vUV` is fed because the shared varying struct carries it. The fill reads
 * no uv, and a varying the vertex stage leaves alone is still a member the
 * fragment stage may read.
 */
export const GEO_FILL_VERTEX_WGSL = `
@vertex fn vs(a: VsIn) -> VsOut {
  var o: VsOut;
  o.vWorld = a.aPos;
  o.vUV = vec2f(0.0);
  o.pos = vec4f((U.uViewProj * vec3f(a.aPos, 1.0)).xy, 0.0, 1.0);
  return o;
}
`;

/**
 * The fill's fragment entry: the wash, cut by the rock mask (R103) and
 * faded by the arrival (R1578).
 */
export const GEO_FILL_FRAG_WGSL = `
@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  let px = worldPx();
  let water = rockPlaneWater(v.vWorld, px);
  let a = U.uTintAlpha * water;
  return vec4f(U.uPalette[U.uFaction].rgb * a, a) * U.uArrival;
}
`;

// ---------------------------------------------------------------------------
// The stroke
// ---------------------------------------------------------------------------

/**
 * The stroke's varying struct.
 *
 * The shared struct of `./prelude.ts` carries the world point and the uv. The
 * stroke carries two more varyings: the signed distance to the outline in
 * world px, and the place along the loop in dash pattern units (R1577). So
 * it states the whole struct here instead. One struct serves both stages,
 * which is why it cannot be extended from the outside.
 */
export const GEO_STROKE_VARYINGS_WGSL = `struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) vWorld: vec2f,
  @location(1) vUV: vec2f,
  @location(2) vD: f32,
  @location(3) vS: f32,
}
`;

/**
 * The stroke's vertex entry. Its input struct is generated from the list.
 *
 * `aNormal` is the unit-scale miter normal the trace computed and `aSide` is
 * which edge of the strip this vertex is, so the half width is applied here
 * and the fragment reads a signed distance to the outline in world px.
 * `aArc` is the length along the loop and the length of the loop, which the
 * faction dash reads (R1577; the GLSL half states the stretch).
 */
export const GEO_STROKE_VERTEX_WGSL = `
@vertex fn vs(a: VsIn) -> VsOut {
  var o: VsOut;
  // The GLSL half names it halfW, because \`half\` is a reserved word there.
  // The twin keeps the name of the line it was ported from.
  let halfW = 0.5 * U.uStripWidth * U.uWidthScale + U.uAaPad;
  // aNormal is signed by the side, so aNormal * aSide is the inward normal
  // on both edges of the strip, and the inset moves the whole strip in.
  let p = a.aPos + a.aNormal * (halfW + a.aSide * U.uInset);
  o.vD = a.aSide * halfW;
  // The dash of R1568 runs along the loop (R1577): a whole number of the
  // faction's periods fills the loop, so the pattern closes on itself.
  let period = U.uDashB[U.uFaction].z * U.uWidthScale;
  let loopLen = max(a.aArc.y, 1e-3);
  let fit = select(1.0, max(floor(loopLen / period + 0.5), 1.0) * period / loopLen,
                   period > 0.0);
  o.vS = a.aArc.x * fit / U.uWidthScale;
  // The shared varying struct declares vWorld for every fragment; the stroke
  // reads no world point, but the varying must still be fed.
  o.vWorld = p;
  o.vUV = vec2f(0.0);
  o.pos = vec4f((U.uViewProj * vec3f(p, 1.0)).xy, 0.0, 1.0);
  return o;
}
`;

/** The stroke's fragment entry: the three overlaid passes of the glow. */
export const GEO_STROKE_FRAG_WGSL = `
@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  let px = worldPx();
  let d = abs(v.vD);
  // The LINE pass takes the faction's dash (R1568, R1577): the distance to
  // the nearest run along the loop and the distance to the line make a
  // round-capped dash. The halo and the glow stay solid. It is the third
  // pass, because border_passes is three passes, the line last.
  let dl = length(vec2f(dashAlong(U.uFaction, v.vS), d));
  // Three overlaid same-colour strokes, composed as \`territoryPaint\` does:
  // a sum would pass 1 at the centre, so the passes stack as
  // 1 - prod(1 - a*c). The pass index is a dynamic index of a vec3, which
  // WGSL allows.
  var keep = 1.0;
  for (var q: i32 = 0; q < 3; q++) {
    let dq = select(d, dl, q == 2);
    keep *= 1.0 - U.uPassAlpha[q] * lineCov(dq, 0.5 * U.uPassWidth[q] * U.uWidthScale, px);
  }
  let a = (1.0 - keep) * U.uPulse;
  // The arrival fades the glow by the factor that fades the wash (R1578).
  return vec4f(U.uPalette[U.uFaction].rgb * a, a) * U.uArrival;
}
`;

// ---------------------------------------------------------------------------
// The modules
// ---------------------------------------------------------------------------
//
// Each fragment-half builder declares the pass's own block: it is the module
// the parity suite holds the twin against. The engine builds its module
// through `worldProgram` instead, from the whole block with the driver's
// three uniforms first, because both vertex entries read the camera matrix
// out of the same struct.

/** The fill's BODY: the shared prelude, the rock mask and the entry. */
export function geoFillBodyWgsl(): string {
  return PRELUDE_BODY_WGSL + ROCK_PLANE_WGSL + GEO_FILL_FRAG_WGSL;
}

/** The fill's fragment half: the header, the body, no vertex entry. */
export function geoFillFragWgsl(): string {
  return wgslModule({
    uniforms: GEO_FILL_ALL_UNIFORMS, attribs: GEO_FILL_ATTRIBS, body: geoFillBodyWgsl(),
  });
}

/** The stroke's BODY: the shared prelude, the dash arithmetic and the entry. */
export function geoStrokeBodyWgsl(): string {
  return PRELUDE_BODY_WGSL + DASH_ALONG_WGSL + GEO_STROKE_FRAG_WGSL;
}

/**
 * The stroke's fragment half.
 *
 * It hands the assembly rule a varying struct of its own, because the stroke
 * carries a third varying. The fill keeps the shared struct.
 */
export function geoStrokeFragWgsl(): string {
  return wgslModule({
    uniforms: GEO_STROKE_ALL_UNIFORMS, attribs: GEO_STROKE_ATTRIBS,
    varyings: GEO_STROKE_VARYINGS_WGSL, body: geoStrokeBodyWgsl(),
  });
}
